var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { merge, isNullOrUndefined, extend, Property } from '@syncfusion/ej2-base';
import { Query, DataUtil } from '@syncfusion/ej2-data';
import { ValueFormatter } from '../services/value-formatter';
import { getUid, templateCompiler, getForeignData, getObject } from '../base/util';
/**
 * Represents Grid `Column` model class.
 */
export class Column {
    constructor(options, parent) {
        /**
         * If `disableHtmlEncode` is set to true, it encodes the HTML of the header and content cells.
         *
         * @default true
         */
        this.disableHtmlEncode = true;
        /**
         * Allows grid to perform row spanning on the specified column.
         *
         * @default true
         */
        this.enableRowSpan = true;
        /**
         * Allows grid to perform column spanning on the specified column.
         *
         * @default true
         */
        this.enableColumnSpan = true;
        /**
         * If `allowSorting` set to false, then it disables sorting option of a particular column.
         * By default all columns are sortable.
         *
         * @default true
         */
        this.allowSorting = true;
        /**
         * If `allowResizing` is set to false, it disables resize option of a particular column.
         * By default all the columns can be resized.
         *
         * @default true
         */
        this.allowResizing = true;
        /**
         * If `allowFiltering` set to false, then it disables filtering option and filter bar element of a particular column.
         * By default all columns are filterable.
         *
         * @default true
         */
        this.allowFiltering = true;
        /**
         * If `allowGrouping` set to false, then it disables grouping of a particular column.
         * By default all columns are groupable.
         *
         * @default true
         */
        this.allowGrouping = true;
        /**
         * If `allowReordering` set to false, then it disables reorder of a particular column.
         * By default all columns can be reorder.
         *
         * @default true
         */
        this.allowReordering = true;
        /**
         * If `showColumnMenu` set to false, then it disable the column menu of a particular column.
         * By default column menu will show for all columns
         *
         * @default true
         */
        this.showColumnMenu = true;
        /**
         * If `enableGroupByFormat` set to true, then it groups the particular column by formatted values.
         *
         * @default true
         */
        this.enableGroupByFormat = false;
        /**
         * If `allowEditing` set to false, then it disables editing of a particular column.
         * By default all columns are editable.
         *
         * @default true
         */
        this.allowEditing = true;
        /**
         *  It is used to customize the default filter options for a specific columns.
         * * type -  Specifies the filter type as menu or checkbox.
         * * ui - to render custom component for specific column it has following functions.
         * * ui.create – It is used for creating custom components.
         * * ui.read -  It is used for read the value from the component.
         * * ui.write - It is used to apply component model as dynamically.
         * {% codeBlock src="grid/filter-menu-api/index.ts" %}{% endcodeBlock %}
         *
         * > Check the [`Filter UI`](../../grid/filtering/filter-menu/#custom-component-in-filter-menu) for its customization.
         *
         *  @default {}
         */
        this.filter = {};
        /**
         * If `showInColumnChooser` set to false, then hide the particular column in column chooser.
         *  By default all columns are displayed in column Chooser.
         *
         * @default true
         */
        this.showInColumnChooser = true;
        /**
         * Defines the `IEditCell` object to customize default edit cell.
         *
         * @default {}
         */
        this.edit = {};
        /**
         * If `allowSearching` set to false, then it disables Searching of a particular column.
         * By default all columns allow Searching.
         *
         * @default true
         */
        this.allowSearching = true;
        /**
         * If `autoFit` set to true, then the particular column content width will be
         * adjusted based on its content in the initial rendering itself.
         * Setting this property as true is equivalent to calling `autoFitColumns` method in the `dataBound` event.
         *
         * @default false
         */
        this.autoFit = false;
        this.sortDirection = 'Descending';
        /**
         * @returns {Function} returns the edit template
         * @hidden */
        this.getEditTemplate = () => this.editTemplateFn;
        /**
         * @returns {Function} returns the filter template
         * @hidden */
        this.getFilterTemplate = () => this.filterTemplateFn;
        /**
         * Determines the behavior of the `aria-label` attribute for cells in template columns.
         * If enableAriaLabel is set to false, the aria-label attribute is not applied to template column cells, which affects screen reader accessibility.
         *
         * @default {}
         */
        this.templateOptions = { enableAriaLabel: true };
        merge(this, options);
        this.parent = parent;
        if (this.type === 'none') {
            this.type = null;
        }
        else if (this.type) {
            this.type = typeof (this.type) === 'string' ? this.type.toLowerCase() : undefined;
        }
        if (this.editType) {
            this.editType = this.editType.toLowerCase();
        }
        if (isNullOrUndefined(this.uid)) {
            this.uid = getUid('grid-column');
        }
        const valueFormatter = new ValueFormatter();
        if (options.format && (options.format.skeleton || (options.format.format &&
            typeof options.format.format === 'string'))) {
            this.setFormatter(valueFormatter.getFormatFunction(extend({}, options.format)));
            this.setParser(valueFormatter.getParserFunction(options.format));
        }
        this.toJSON = () => {
            const col = {};
            const skip = ['filter', 'dataSource', 'headerText', 'template', 'headerTemplate', 'edit',
                'editTemplate', 'filterTemplate', 'commandsTemplate', 'parent'];
            const keys = Object.keys(this);
            for (let i = 0; i < keys.length; i++) {
                if (keys[parseInt(i.toString(), 10)] === 'columns') {
                    col[keys[parseInt(i.toString(), 10)]] = [];
                    for (let j = 0; j < this[keys[parseInt(i.toString(), 10)]].length; j++) {
                        col[keys[parseInt(i.toString(), 10)]].push(this[keys[parseInt(i.toString(), 10)]][parseInt(j.toString(), 10)].toJSON());
                    }
                }
                else if (skip.indexOf(keys[parseInt(i.toString(), 10)]) < 0) {
                    col[keys[parseInt(i.toString(), 10)]] = this[keys[parseInt(i.toString(), 10)]];
                }
            }
            return col;
        };
        if (!this.field) {
            this.allowFiltering = false;
            this.allowGrouping = false;
            this.allowSorting = false;
            this.enableColumnSpan = false;
            this.enableRowSpan = false;
            if (this.columns) {
                this.allowResizing = this.columns.some((col) => {
                    return col.allowResizing;
                });
            }
        }
        if (this.commands && !this.textAlign) {
            this.textAlign = 'Right';
        }
        if (this.template || this.commandsTemplate) {
            this.templateFn = templateCompiler(this.template || this.commandsTemplate);
        }
        if (this.headerTemplate) {
            this.headerTemplateFn = templateCompiler(this.headerTemplate);
        }
        if (!isNullOrUndefined(this.filter) && this.filter.itemTemplate) {
            this.fltrTemplateFn = templateCompiler(this.filter.itemTemplate);
        }
        if (this.editTemplate) {
            this.editTemplateFn = templateCompiler(this.editTemplate);
        }
        if (this.filterTemplate) {
            this.filterTemplateFn = templateCompiler(this.filterTemplate);
        }
        if (this.isForeignColumn() &&
            (isNullOrUndefined(this.editType) || this.editType === 'dropdownedit' || this.editType === 'defaultedit')) {
            this.editType = 'dropdownedit';
            if (this.edit.params && this.edit.params.dataSource) {
                this.edit.params.ddEditedData = true;
            }
            this.edit.params = extend({
                dataSource: this.dataSource,
                query: new Query(), fields: { value: this.foreignKeyField || this.field, text: this.foreignKeyValue }
            }, this.edit.params);
        }
        if (this.sortComparer) {
            let a = this.sortComparer;
            this.sortComparer = (x, y, xObj, yObj) => {
                if (typeof a === 'string') {
                    a = getObject(a, window);
                }
                if (this.sortDirection === 'Descending') {
                    const z = x;
                    x = y;
                    y = z;
                    const obj = xObj;
                    xObj = yObj;
                    yObj = obj;
                }
                return a(x, y, xObj, yObj);
            };
        }
        if (!this.sortComparer && this.isForeignColumn()) {
            this.sortComparer = (x, y) => {
                x = getObject(this.foreignKeyValue, getForeignData(this, {}, x)[0]);
                y = getObject(this.foreignKeyValue, getForeignData(this, {}, y)[0]);
                return this.sortDirection === 'Descending' ? DataUtil.fnDescending(x, y) : DataUtil.fnAscending(x, y);
            };
        }
    }
    /**
     * @returns {string} returns the sort direction
     * @hidden */
    getSortDirection() {
        return this.sortDirection;
    }
    /**
     * @param {string} direction - specifies the direction
     * @returns {void}
     * @hidden
     */
    setSortDirection(direction) {
        this.sortDirection = direction;
    }
    /**
     * @returns {freezeTable} returns the FreezeTable
     * @hidden */
    getFreezeTableName() {
        return this.freezeTable;
    }
    /**
     * @param {Column} column - specifies the column
     * @returns {void}
     * @hidden
     */
    setProperties(column) {
        //Angular two way binding
        const keys = Object.keys(column);
        for (let i = 0; i < keys.length; i++) {
            if (keys[parseInt(i.toString(), 10)] === 'columns') {
                const cols = column[keys[parseInt(i.toString(), 10)]];
                for (let j = 0; j < cols.length; j++) {
                    this.columns.find((col) => {
                        return col.field === cols[parseInt(j.toString(), 10)]
                            .field;
                    }).setProperties(cols[parseInt(j.toString(), 10)]);
                }
            }
            else {
                this[keys[parseInt(i.toString(), 10)]] = column[keys[parseInt(i.toString(), 10)]];
            }
            //Refresh the react columnTemplates on state change
            if (this.parent && this.parent.isReact) {
                if (keys[parseInt(i.toString(), 10)] === 'template') {
                    this.templateFn = templateCompiler(column[keys[parseInt(i.toString(), 10)]]);
                    this.parent.refreshReactColumnTemplateByUid(this.uid, true);
                }
                else if (keys[parseInt(i.toString(), 10)] === 'headerTemplate') {
                    this.headerTemplateFn = templateCompiler(column[keys[parseInt(i.toString(), 10)]]);
                    this.parent.refreshReactHeaderTemplateByUid(this.uid);
                }
                else if (keys[parseInt(i.toString(), 10)] === 'editTemplate') {
                    this.editTemplateFn = templateCompiler(column[keys[parseInt(i.toString(), 10)]]);
                }
                else if (keys[parseInt(i.toString(), 10)] === 'filterTemplate') {
                    this.filterTemplateFn = templateCompiler(column[keys[parseInt(i.toString(), 10)]]);
                }
                else if (keys[parseInt(i.toString(), 10)] === 'commandsTemplate') {
                    this.templateFn = templateCompiler(column[keys[parseInt(i.toString(), 10)]]);
                }
            }
        }
    }
    /**
     * @returns {boolean} returns true for foreign column
     * @hidden
     * It defines the column is foreign key column or not.
     */
    isForeignColumn() {
        return !!(this.dataSource && this.foreignKeyValue);
    }
    /**
     * @returns {Function} returns the function
     * @hidden
     */
    getFormatter() {
        return this.formatFn;
    }
    /**
     * @param {Function} value - specifies the value
     * @returns {void}
     * @hidden
     */
    setFormatter(value) {
        this.formatFn = value;
    }
    /**
     * @returns {Function} returns the function
     * @hidden */
    getParser() {
        return this.parserFn;
    }
    /**
     * @param {Function} value - specifies the value
     * @returns {void}
     * @hidden
     */
    setParser(value) {
        this.parserFn = value;
    }
    /**
     * @returns {Function} returns the function
     * @hidden */
    getColumnTemplate() {
        return this.templateFn;
    }
    /**
     * @returns {Function} returns the function
     * @hidden */
    getHeaderTemplate() {
        return this.headerTemplateFn;
    }
    /**
     * @returns {Function} returns the function
     * @hidden */
    getFilterItemTemplate() {
        return this.fltrTemplateFn;
    }
    /**
     * @returns {string} returns the string
     * @hidden */
    getDomSetter() {
        return this.disableHtmlEncode ? 'textContent' : 'innerHTML';
    }
}
/**
 * Define options for custom command buttons.
 */
export class CommandColumnModel {
}
__decorate([
    Property()
], CommandColumnModel.prototype, "title", void 0);
__decorate([
    Property()
], CommandColumnModel.prototype, "type", void 0);
__decorate([
    Property()
], CommandColumnModel.prototype, "buttonOption", void 0);
/**
 * Defines Grid column
 */
export class GridColumn extends Column {
}
__decorate([
    Property(null)
], GridColumn.prototype, "columns", void 0);
/**
 * Defines stacked grid column
 */
export class StackedColumn extends GridColumn {
}
